// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { warnOnce } from '@cloudscape-design/component-toolkit/internal';
export const applyTrackBy = (trackBy, item) => {
    if (typeof trackBy === 'function') {
        return trackBy(item);
    }
    return item[trackBy];
};
export const getItemKey = (trackBy, item, index) => {
    if (!trackBy) {
        return index;
    }
    return applyTrackBy(trackBy, item);
};
export const getTrackableValue = (trackBy, item) => {
    if (!trackBy) {
        return item;
    }
    return applyTrackBy(trackBy, item);
};
export const getColumnKey = (column, index) => {
    return column.id || index;
};
export const toContainerVariant = (variant) => {
    const isDefaultVariant = !variant || variant === 'container';
    return isDefaultVariant ? 'default' : variant === 'borderless' ? 'embedded' : variant;
};
export function checkSortingState(columnDefinitions, sortingComparator) {
    const matchedColumn = columnDefinitions.filter(column => column.sortingComparator === sortingComparator)[0];
    if (!matchedColumn) {
        warnOnce('Table', 'Currently active sorting comparator was not found in any columns. Make sure to provide the same comparator function instance on each render.');
    }
}
export function getVisibleColumnDefinitions({ columnDisplay, visibleColumns, columnDefinitions, }) {
    // columnsDisplay has a precedence over visibleColumns.
    if (columnDisplay) {
        return getVisibleColumnDefinitionsFromColumnDisplay({ columnDisplay, columnDefinitions });
    }
    else if (visibleColumns) {
        return getVisibleColumnDefinitionsFromVisibleColumns({ visibleColumns, columnDefinitions });
    }
    else {
        return columnDefinitions;
    }
}
function getVisibleColumnDefinitionsFromColumnDisplay({ columnDisplay, columnDefinitions, }) {
    const columnDefinitionsById = columnDefinitions.reduce((accumulator, item) => (item.id === undefined ? accumulator : { ...accumulator, [item.id]: item }), {});
    const visibleIds = flattenVisibleColumnIds(columnDisplay);
    return visibleIds.map(id => columnDefinitionsById[id]).filter(Boolean);
}
function getVisibleColumnDefinitionsFromVisibleColumns({ visibleColumns, columnDefinitions, }) {
    const ids = new Set(visibleColumns);
    return columnDefinitions.filter(({ id }) => id !== undefined && ids.has(id));
}
export function getStickyClassNames(styles, props) {
    const classNames = {};
    const addClassName = (name, isActive) => {
        if (styles[name] !== undefined) {
            classNames[styles[name]] = isActive;
        }
    };
    addClassName('sticky-cell', !!props);
    addClassName('sticky-cell-pad-inline-start', !!(props === null || props === void 0 ? void 0 : props.padInlineStart));
    addClassName('sticky-cell-last-inline-start', !!(props === null || props === void 0 ? void 0 : props.lastInsetInlineStart));
    addClassName('sticky-cell-last-inline-end', !!(props === null || props === void 0 ? void 0 : props.lastInsetInlineEnd));
    addClassName('sticky-cell-boundary-inline-start', !!(props === null || props === void 0 ? void 0 : props.boundaryInlineStart));
    addClassName('sticky-cell-boundary-inline-end', !!(props === null || props === void 0 ? void 0 : props.boundaryInlineEnd));
    return classNames;
}
function flattenVisibleColumnIds(items) {
    const ids = [];
    for (const item of items) {
        if (item.type === 'group') {
            ids.push(...flattenVisibleColumnIds(item.children));
        }
        else if (item.visible) {
            ids.push(item.id);
        }
    }
    return ids;
}
//# sourceMappingURL=utils.js.map